import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { Loader2, Trophy, TrendingUp, Radio } from "lucide-react";
import { api } from "../lib/api";
import { TeamLogo } from "./TeamLogo";
import { TeamPropProjections } from "./TeamPropProjections";

const TeamBar = ({ team, pct, scoreLabel, winner }) => (
  <div className="flex-1 min-w-0">
    <div className="flex items-center justify-between gap-1 mb-1.5">
      <div className="flex items-center gap-2 min-w-0">
        <TeamLogo sport={team.sport} abbr={team.abbr} color={team.color} size="md" />
        <span className="text-sm text-white font-semibold truncate">{team.name}</span>
        {winner && <Trophy className="h-3.5 w-3.5 text-[#F59E0B]" />}
      </div>
      <span className="font-data font-bold text-base sm:text-lg shrink-0" style={{ color: winner ? "#10B981" : "#94A3B8" }}>
        {pct}%
      </span>
    </div>
    <div className="h-2 rounded-full bg-[#233248] overflow-hidden">
      <motion.div
        animate={{ width: `${pct}%` }}
        transition={{ duration: 0.9, ease: "easeOut" }}
        className="h-full rounded-full"
        style={{ background: winner ? "#10B981" : team.color }}
      />
    </div>
    {scoreLabel != null && (
      <div className="font-data text-[11px] text-slate-500 mt-1">{scoreLabel}</div>
    )}
  </div>
);

export const WinProjection = ({ matchupId, homeTeam, awayTeam }) => {
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);
  const [live, setLive] = useState(null);

  useEffect(() => {
    setLoading(true);
    setData(null);
    setError(null);
    api
      .post("/ai/predict", { matchup_id: matchupId })
      .then((r) => setData(r.data))
      .catch((e) => setError(e.response?.data?.detail || "Could not project this game."))
      .finally(() => setLoading(false));
  }, [matchupId]);

  useEffect(() => {
    let active = true;
    setLive(null);
    const fetchLive = () => {
      api
        .get(`/matchups/${matchupId}/live-winprob`)
        .then((r) => active && setLive(r.data.live ? r.data : null))
        .catch(() => {});
    };
    fetchLive();
    const t = setInterval(fetchLive, 30000);
    return () => {
      active = false;
      clearInterval(t);
    };
  }, [matchupId]);

  const isLive = !!live;
  const homePct = isLive ? live.home_win_pct : data?.home_win_pct;
  const awayPct = isLive ? live.away_win_pct : data?.away_win_pct;
  const homeWins = isLive ? homePct > awayPct : data && data.predicted_winner === homeTeam.abbr;
  const awayWins = isLive ? awayPct > homePct : data && data.predicted_winner === awayTeam.abbr;
  const homeScoreLabel = isLive ? `now ${live.home_score}` : data && data.projected_home_score != null ? `proj ${data.projected_home_score}` : null;
  const awayScoreLabel = isLive ? `now ${live.away_score}` : data && data.projected_away_score != null ? `proj ${data.projected_away_score}` : null;
  const showBars = isLive || (data && !loading);

  return (
    <div
      data-testid="win-projection"
      className="rounded-2xl border border-[#233248] bg-gradient-to-b from-[#141C2B] to-[#0e1826] p-5 mb-5"
    >
      <div className="flex items-center gap-2 mb-4 flex-wrap">
        <div className="h-7 w-7 rounded-lg bg-[#F59E0B]/15 grid place-items-center">
          <TrendingUp className="h-4 w-4 text-[#F59E0B]" />
        </div>
        <h3 className="font-heading font-bold text-white text-base">Win Projection</h3>
        {isLive ? (
          <span
            data-testid="winprob-live-badge"
            className="flex items-center gap-1.5 rounded-md bg-[#EF4444]/15 text-red-400 text-[10px] font-bold px-2 py-1 font-data uppercase tracking-wide"
          >
            <Radio className="h-3 w-3 animate-pulse" />
            Live · {live.clock && live.period ? `${live.clock} · ` : ""}{live.short_detail}
          </span>
        ) : (
          <span className="font-data text-[10px] text-slate-500">AI · based on team stats & lines</span>
        )}
      </div>

      {loading && !isLive && (
        <div className="flex items-center gap-2 py-4 text-slate-400 text-sm">
          <Loader2 className="h-4 w-4 animate-spin text-[#00E5FF]" /> Projecting the matchup…
        </div>
      )}

      {error && !loading && !isLive && <p className="text-sm text-slate-500 py-2">{error}</p>}

      {showBars && (
        <>
          <div className="flex items-center gap-2 sm:gap-4">
            <TeamBar team={awayTeam} pct={awayPct} scoreLabel={awayScoreLabel} winner={awayWins} />
            <span className="font-heading font-bold text-slate-600 text-xs sm:text-sm shrink-0">VS</span>
            <TeamBar team={homeTeam} pct={homePct} scoreLabel={homeScoreLabel} winner={homeWins} />
          </div>

          {isLive ? (
            <p data-testid="winprob-live-note" className="text-[11px] text-slate-500 mt-3 font-data">
              Live win probability — updates every 30s from the current score &amp; time remaining.
            </p>
          ) : (
            <>
              <div className="mt-4 flex items-center gap-2 flex-wrap">
                <span className="rounded-md bg-[#10B981]/15 text-[#10B981] text-xs font-semibold px-2 py-1">
                  Pick: {data.predicted_winner}
                </span>
                <span className="rounded-md bg-[#1B273E] text-slate-300 text-xs px-2 py-1 font-data">
                  {data.confidence}% confidence
                </span>
              </div>

              {data.reasoning && <p className="text-sm text-slate-300 mt-3 leading-relaxed">{data.reasoning}</p>}

              {data.key_factors?.length > 0 && (
                <div className="flex flex-wrap gap-1.5 mt-3">
                  {data.key_factors.map((f, i) => (
                    <span key={i} className="rounded-md bg-[#0B0F17] border border-[#233248] px-2 py-0.5 text-[11px] text-slate-400">
                      {f}
                    </span>
                  ))}
                </div>
              )}
            </>
          )}
        </>
      )}

      {homeTeam.sport === "NFL" && <TeamPropProjections matchupId={matchupId} homeTeam={homeTeam} awayTeam={awayTeam} />}
    </div>
  );
};
